<template>
  <div class="flex flex-col h-full">
    <div class="flex mb-4 gap-2">
      <payment-method-selector
        class="flex-1"
        :payment-methods="paymentMethods"
        @change="changePaymentMethod"
      />
      <div
        v-if="showDiscounts"
        class="border border-n-600 px-4 flex-none text-n-0 rounded-lg cursor-pointer bg-n-700 flex items-center justify-center"
        @click="$emit('showDiscounts')"
      >
        <l-icon name="discount" />
      </div>
    </div>
    <probable-amounts-selector
      v-if="showProbableAmounts"
      class="mb-4"
      :initial-amount="initialPending"
      :actual-amount="calculatorValues.toPay"
      :tip-mode="calculatorTipMode"
      @change="onChangeProbableAmount"
    />
    <calculator
      ref="calculator"
      class="mb-2 h-full"
      :initial-to-pay="selectedFixedAmount"
      :pending="initialPending"
      :display-mode="displayMode"
      :keypad-mode="keypadMode"
      :tip-mode="calculatorTipMode"
      :keep-divisions="true"
      @change="calculatorChange"
      @division="onCalculatorDivision"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { LIcon } from '@last/core-ui/paprika'

import type { PaymentMethod } from '@/types'

import Calculator from '../Calculator/Calculator.vue'
import type {
  CalculatorDisplayModeType,
  CalculatorKeypadModeType,
  CalculatorPayload
} from '../Calculator/types'
import { CalculatorDisplayMode, CalculatorKeypadMode } from '../Calculator/types'
import PaymentMethodSelector from './PaymentMethodSelector.vue'
import ProbableAmountsSelector from './ProbableAmountsSelector.vue'

type Props = {
  paymentMethods: PaymentMethod[]
  initialPending?: number
  showProbableAmounts?: boolean
  displayMode?: CalculatorDisplayModeType
  keypadMode?: CalculatorKeypadModeType
  autofill?: boolean
  showDiscounts?: boolean
  keepDivisions?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  initialPending: 0,
  showProbableAmounts: true,
  keypadMode: CalculatorKeypadMode.SIMPLE,
  displayMode: CalculatorDisplayMode.CHANGE,
  autofill: true,
  showDiscounts: false,
  keepDivisions: false
})

const emit = defineEmits<{
  changePaymentMethod: [string]
  calculatorChange: [CalculatorPayload]
  showDiscounts: []
  division: [number]
}>()

const calculatorValues = ref<CalculatorPayload>({
  toPay: 0,
  pending: 0,
  change: 0,
  tip: 0
})

const selectedFixedAmount = ref<number>(0)
const paymentMethod = ref<PaymentMethod>(props.paymentMethods[0])

const calculatorTipMode = computed(() => {
  return paymentMethod.value.type !== 'cash'
})

const lastDivisionValue = ref<number>(0)
watch(
  () => props.initialPending,
  value => {
    if (props.autofill) {
      selectedFixedAmount.value = value
      calculatorValues.value.toPay = value
    }
    if (props.keypadMode === 'full') {
      if (lastDivisionValue.value > 0) {
        selectedFixedAmount.value = Math.min(lastDivisionValue.value, value)
      } else {
        selectedFixedAmount.value = 0
      }
    }
    calculatorValues.value.pending = value
  },
  { immediate: true }
)

function onCalculatorDivision(value: number) {
  lastDivisionValue.value = value
  emit('division', value)
}

function calculatorChange(value: CalculatorPayload) {
  calculatorValues.value = value
  emit('calculatorChange', value)
}

function changePaymentMethod(method: string): void {
  paymentMethod.value = props.paymentMethods.find(m => m.type === method) || props.paymentMethods[0]
  emit('changePaymentMethod', method)
}

function onChangeProbableAmount(amount: number): void {
  selectedFixedAmount.value = amount
}
</script>
